import { forwardRef, useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react'
import classNames from 'classnames'
import { useConfig } from '../ConfigProvider'
import { useForm } from '../Form/context'
import { useInputGroup } from '../InputGroup/context'
import { CONTROL_SIZES } from '../utils/constants'
import Tooltip from '../Tooltip'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import get from 'lodash/get'
import type { CommonProps, TypeAttributes } from '../@types/common'
import type {
    InputHTMLAttributes,
    ElementType,
    ReactNode,
    HTMLInputTypeAttribute,
} from 'react'

export interface InputProps
    extends CommonProps,
        Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
    asElement?: ElementType
    disabled?: boolean
    invalid?: boolean
    affixGap?: number
    affixPaddingX?: number
    contentIndent?: number
    prefix?: string | ReactNode
    rows?: number
    size?: TypeAttributes.ControlSize
    suffix?: string | ReactNode
    textArea?: boolean
    type?: HTMLInputTypeAttribute
    unstyle?: boolean
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    field?: any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form?: any
}

const Input = forwardRef<HTMLInputElement, InputProps>(
    (props, ref) => {
        const {
            asElement: Component = 'input',
            affixGap = 8,
            affixPaddingX = 8,
            contentIndent,
            className,
            disabled,
            invalid,
            prefix,
            size,
            suffix,
            textArea,
            type = 'text',
            rows,
            style,
            unstyle = false,
            field,
            form,
            ...rest
        } = props

        const [prefixGutter, setPrefixGutter] = useState(0)
        const [suffixGutter, setSuffixGutter] = useState(0)

        const { themeColor, controlSize, primaryColorLevel, direction } =
            useConfig()
        const formControlSize = useForm()?.size
        const inputGroupSize = useInputGroup()?.size

        const inputSize =
            size || inputGroupSize || formControlSize || controlSize

        const fixControlledValue = (
            val: string | number | readonly string[] | undefined,
        ) => {
            if (typeof val === 'undefined' || val === null) {
                return ''
            }
            return val
        }

        if ('value' in props) {
            rest.value = fixControlledValue(props.value)
            delete rest.defaultValue
        }

        const isInvalid = useMemo(() => {
            let validate = false
            if (!isEmpty(form)) {
                const { touched, errors } = form
                const touchedField = get(touched, field.name)
                const errorField = get(errors, field.name)
                validate = touchedField && errorField
            }
            if (typeof invalid === 'boolean') {
                validate = invalid
            }
            return validate
        }, [form, invalid, field])

        const inputDefaultClass = 'input'
        const inputHeightMap: Record<string, number> = { ...CONTROL_SIZES, md: 8 }
        const inputSizeClass = `input-${inputSize} h-${inputHeightMap[inputSize] ?? CONTROL_SIZES[inputSize]}`
        const inputFocusClass = `focus:ring-${themeColor}-${primaryColorLevel} focus-within:ring-${themeColor}-${primaryColorLevel} focus-within:border-${themeColor}-${primaryColorLevel} focus:border-${themeColor}-${primaryColorLevel}`
        const inputWrapperClass = `input-wrapper ${
            prefix || suffix ? className : ''
        }`
        const inputClass = classNames(
            inputDefaultClass,
            !textArea && inputSizeClass,
            !isInvalid && inputFocusClass,
            !prefix && !suffix ? className : '',
            disabled && 'input-disabled',
            isInvalid && 'input-invalid',
            textArea && 'input-textarea',
        )

        const prefixNode = useRef<HTMLDivElement>(null)
        const suffixNode = useRef<HTMLDivElement>(null)

        const getAffixSize = () => {
            if (!prefixNode.current && !suffixNode.current) {
                return
            }
            const prefixNodeWidth = prefixNode?.current?.offsetWidth
            const suffixNodeWidth = suffixNode?.current?.offsetWidth

            if (isNil(prefixNodeWidth) && isNil(suffixNodeWidth)) {
                return
            }

            if (prefixNodeWidth) {
                setPrefixGutter(prefixNodeWidth)
            }

            if (suffixNodeWidth) {
                setSuffixGutter(suffixNodeWidth)
            }
        }

        useEffect(() => {
            getAffixSize()
        }, [prefix, suffix])

        // ── Disabled tooltip ──────────────────────────────────────────────────
        const tooltipValue = String(rest.value ?? field?.value ?? '')

        // Internal ref for overflow measurement; merged with the forwarded ref
        const internalInputRef = useRef<HTMLElement | null>(null)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const mergedRef = (el: any) => {
            internalInputRef.current = el
            if (!ref) return
            if (typeof ref === 'function') ref(el)
            else (ref as { current: typeof el }).current = el
        }

        const [inputOverflows, setInputOverflows] = useState(false)

        useLayoutEffect(() => {
            const shouldCheck = !!(disabled && !textArea && type !== 'password')
            if (!shouldCheck) {
                setInputOverflows(false)
                return
            }
            const el = internalInputRef.current
            if (!el) return
            const checkOverflow = () => {
                const style = window.getComputedStyle(el)
                const paddingLeft = parseFloat(style.paddingLeft) || 0
                const paddingRight = parseFloat(style.paddingRight) || 0
                const availableWidth = el.offsetWidth - paddingLeft - paddingRight
                const canvas = document.createElement('canvas')
                const ctx = canvas.getContext('2d')
                if (!ctx) { setInputOverflows(false); return }
                ctx.font = style.font
                const textWidth = ctx.measureText(tooltipValue).width
                setInputOverflows(textWidth > availableWidth)
            }
            checkOverflow()
            const ro = new ResizeObserver(checkOverflow)
            ro.observe(el)
            return () => ro.disconnect()
        }, [disabled, textArea, type, tooltipValue, inputOverflows])

        const showDisabledTooltip = disabled && !textArea && type !== 'password' && inputOverflows

        const affixGutterStyle = () => {
            const basePx = style?.padding !== undefined ? parseFloat(String(style.padding)) : 0
            const baseLeftPx  = style?.paddingLeft  !== undefined ? parseFloat(String(style.paddingLeft))  : basePx
            const baseRightPx = style?.paddingRight !== undefined ? parseFloat(String(style.paddingRight)) : basePx

            const gutterStyle: { paddingLeft?: string; paddingRight?: string } = {}
            const hasAffix = !!(prefix || suffix)
            if (!hasAffix) return gutterStyle

            if (contentIndent !== undefined) {
                // Fixed text-start mode: all inputs align their text at `contentIndent` px
                // from the input edge. Gracefully falls back to the natural minimum when
                // the prefix/suffix element is wider than the indent allows.
                const leftFinal  = Math.max(contentIndent, affixPaddingX + prefixGutter)
                const rightFinal = Math.max(contentIndent, affixPaddingX + suffixGutter)

                if (direction === 'ltr') {
                    gutterStyle.paddingLeft  = `${Math.max(leftFinal,  baseLeftPx)}px`
                    gutterStyle.paddingRight = `${Math.max(suffix ? rightFinal : affixPaddingX, baseRightPx)}px`
                } else {
                    gutterStyle.paddingRight = `${Math.max(leftFinal,  baseRightPx)}px`
                    gutterStyle.paddingLeft  = `${Math.max(suffix ? rightFinal : affixPaddingX, baseLeftPx)}px`
                }
            } else {
                // Variable-gap mode: text starts at affixPaddingX + elementWidth + affixGap.
                const leftMinPx  = affixPaddingX + prefixGutter + affixGap
                const rightMinPx = affixPaddingX + suffixGutter + affixGap

                if (direction === 'ltr') {
                    gutterStyle.paddingLeft  = `${Math.max(prefix ? leftMinPx  : affixPaddingX, baseLeftPx)}px`
                    gutterStyle.paddingRight = `${Math.max(suffix ? rightMinPx : affixPaddingX, baseRightPx)}px`
                } else {
                    gutterStyle.paddingRight = `${Math.max(prefix ? leftMinPx  : affixPaddingX, baseRightPx)}px`
                    gutterStyle.paddingLeft  = `${Math.max(suffix ? rightMinPx : affixPaddingX, baseLeftPx)}px`
                }
            }

            return gutterStyle
        }

        const inputProps = {
            className: !unstyle ? inputClass : '',
            disabled,
            type,
            ref: mergedRef,
            ...field,
            ...rest,
        }

        const renderTextArea = (
            <textarea style={style} rows={rows} {...inputProps}></textarea>
        )

        const disabledEllipsisStyle: React.CSSProperties =
            disabled && !textArea && type !== 'password'
                ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
                : {}

        const renderInput = (
            <Component
                style={{ ...disabledEllipsisStyle, ...style, ...affixGutterStyle() }}
                {...inputProps}
            />
        )

        const prefixStyle = direction === 'ltr'
            ? { left: affixPaddingX } : { right: affixPaddingX }
        const suffixStyle = direction === 'ltr'
            ? { right: affixPaddingX } : { left: affixPaddingX }

        const renderAffixInput = (
            <span className={inputWrapperClass}>
                {prefix ? (
                    <div ref={prefixNode} className="input-suffix-start" style={prefixStyle}>
                        {' '}
                        {prefix}{' '}
                    </div>
                ) : null}
                {renderInput}
                {suffix ? (
                    <div ref={suffixNode} className="input-suffix-end" style={suffixStyle}>
                        {suffix}
                    </div>
                ) : null}
            </span>
        )

        const renderChildren = () => {
            if (textArea) {
                return renderTextArea
            }

            const inputNode = prefix || suffix ? renderAffixInput : renderInput

            if (showDisabledTooltip) {
                return (
                    <Tooltip
                        title={tooltipValue}
                        placement="top"
                        wrapperClass="input-tooltip-wrapper w-full"
                    >
                        {inputNode}
                    </Tooltip>
                )
            }

            return inputNode
        }

        return renderChildren()
    },
)

Input.displayName = 'Input'

export default Input
